import { UpGradientIcon } from "@/components/Icons/UpGradientIcon";
import { PlayerImage } from "@/components/PlayerImage";
import { Separator } from "@/components/ui/separator";
import { useHandleError } from "@/hooks/useHandleError";
import { useAuthStore } from "@/store/auth.store";
import { useEntryStore } from "@/store/entry.store";
import { CaptainIcon, ThumbaIcon, ViceCaptainIcon } from "@roman/shared/components/Icons";
import LoadingSpinner from "@roman/shared/components/LoadingSpinner";
import { useCreateEntry } from "@roman/shared/hooks/soccer/useCreateEntry";
import { useUserSoccerRoundDetails } from "@roman/shared/hooks/soccer/useSoccerUserRoundDetails";
import type { CreateEntry } from "@roman/shared/types/CreateEntry";
import type { Player } from "@roman/shared/types/Player";
import { cn } from "@roman/shared/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MatchName } from "../PlayerCard/components/MatchName";
import { PlayerDetailButton } from "../PlayerCard/components/PlayerDetailButton";
import { RoundInfo } from "../SelectPlayers";
import { getEntryDetailType } from "../constant";
import { ConfirmCreateEntryModal } from "./components/ConfirmCreateEntryModal";
import { CreateEntrySuccessfulModal } from "./components/CreateEntrySuccessfulModal";
import { EntryLimitReachedModal } from "./components/EntryLimitReachedModal";
import { InsufficientModal } from "./components/InsufficientModal";
import { RequiredLoginModal } from "./components/RequiredLoginModal";

export const SelectCaptain = () => {
  const navigate = useNavigate();
  const entryDetails = useEntryStore((state) => state.entryDetails);
  const setEntryDetails = useEntryStore((state) => state.setEntryDetails);
  const resetAllEntryStore = useEntryStore((state) => state.resetAll);
  const fetchUser = useAuthStore((state) => state.fetchAndSetUser);

  const [openRequiredLoginModal, setOpenRequiredLoginModal] = useState(false);
  const [openConfirmCreateEntryModal, setOpenConfirmCreateEntryModal] = useState(false);
  const [openCreateEntrySuccessfulModal, setOpenCreateEntrySuccessfulModal] = useState(false);
  const [openInsufficientModal, setOpenInsufficientModal] = useState(false);
  const [openEntryLimitReachedModal, setOpenEntryLimitReachedModal] = useState(false);

  const authState = useAuthStore((state) => state.authState);
  const user = useAuthStore((state) => state.user);
  const round = useEntryStore((state) => state.round);

  const userPoints = user ? Number.parseInt(user.freePoints) + Number.parseInt(user.redeemablePoints) : 0;

  const { roundId } = useParams();

  useEffect(() => {
    if (!round) {
      navigate(`/rounds/${roundId}/select-players`);
    }
  }, [round, roundId, navigate]);

  const roundEntryUnit = round?.roundEntryUnits[0];
  if (!roundEntryUnit) {
    return <LoadingSpinner />;
  }

  const getPlayerById = useCallback((id: string | null, players: Player[]) => {
    if (!id) return null;
    return players.find((player) => player.id === id) ?? null;
  }, []);

  const [captain, setCaptain] = useState<Player | null>(() =>
    getPlayerById(entryDetails.detail.captainPlayerId, [...round.players.homePlayers, ...round.players.awayPlayers]),
  );
  const [viceCaptain, setViceCaptain] = useState<Player | null>(() => {
    if (entryDetails.detail.type === "0002") {
      return getPlayerById(entryDetails.detail.viceCaptainPlayerId, [
        ...round.players.homePlayers,
        ...round.players.awayPlayers,
      ]);
    }
    return null;
  });
  const { trigger: createEntry } = useCreateEntry(useHandleError("エントリーの作成に失敗しました"));

  const userRoundHandleError = useHandleError("ユーザーのラウンド詳細の取得に失敗しました");
  const { data: userRoundDetailData, mutate: refetchUserRoundUnit } = useUserSoccerRoundDetails(
    round.id,
    user?.id || "",
    userRoundHandleError,
  );

  const userNumEntries = useMemo(() => {
    if (authState !== "authenticated") {
      return 0;
    }
    return userRoundDetailData?.userRound.roundEntryUnits[0].userNumEntries || 0;
  }, [authState, userRoundDetailData]);

  if (!userRoundDetailData) {
    return <LoadingSpinner />;
  }

  const onSelectCaptain = (player: Player | null) => {
    if (!player) {
      setEntryDetails({
        ...entryDetails,
        detail: {
          ...entryDetails.detail,
          captainPlayerId: "",
        },
      });
      setCaptain(null);
      return;
    }
    setEntryDetails({
      ...entryDetails,
      detail: {
        ...entryDetails.detail,
        captainPlayerId: player.id,
      },
    });
    setCaptain(player);
  };

  const onSelectViceCaptain = (player: Player | null) => {
    if (!player) {
      setEntryDetails({
        ...entryDetails,
        detail: {
          ...entryDetails.detail,
          type: "0002",
          viceCaptainPlayerId: "",
        },
      });

      setViceCaptain(null);
      return;
    }
    const type = getEntryDetailType(round.type);

    if (type === "0002") {
      setEntryDetails({
        ...entryDetails,
        detail: {
          ...entryDetails.detail,
          type: "0002",
          viceCaptainPlayerId: player.id,
        },
      });
      setViceCaptain(player);
    }
  };

  const isEnterDisabled: boolean = (() => {
    switch (round.type) {
      case "Round0001": {
        if (!captain) return true;
        return false;
      }
      case "Round0002": {
        if (!captain || !viceCaptain) return true;
        return false;
      }
      default:
        return true;
    }
  })();

  const handleOnClick = () => {
    if (isEnterDisabled) {
      return;
    }
    if (authState !== "authenticated") {
      setOpenRequiredLoginModal(true);
      return;
    }

    if (userPoints < Number.parseInt(roundEntryUnit.entryFee)) {
      setOpenInsufficientModal(true);
      return;
    }

    const maxNumEntriesPerUser = roundEntryUnit.maxNumEntriesPerUser;

    if (maxNumEntriesPerUser && userNumEntries >= maxNumEntriesPerUser) {
      setOpenEntryLimitReachedModal(true);
      return;
    }

    setOpenConfirmCreateEntryModal(true);
  };

  const handleConfirmCreateEntry = async () => {
    if (!entryDetails.detail.type || !entryDetails.detail.captainPlayerId) {
      toast.error("ラウンドにエントリーするための情報が不足しています");
      return;
    }

    const entryTx: CreateEntry = {
      ...entryDetails,
      entries: [
        {
          type: "Points",
          roundEntryUnitId: roundEntryUnit.id,
          numEntries: 1,
        },
      ],
      detail: {
        ...entryDetails.detail,
        captainPlayerId: entryDetails.detail.captainPlayerId,
        ...(entryDetails.detail.type === "0002" && {
          viceCaptainPlayerId: entryDetails.detail.viceCaptainPlayerId,
        }),
      },
    };

    setOpenConfirmCreateEntryModal(false);

    await createEntry(
      { entryTx },
      {
        onSuccess: async () => {
          setOpenCreateEntrySuccessfulModal(true);
          await fetchUser();
        },
      },
    );

    refetchUserRoundUnit();
  };

  const handleConfirmCreateEntrySuccessful = () => {
    setOpenCreateEntrySuccessfulModal(false);

    const maxNumEntriesPerUser = roundEntryUnit.maxNumEntriesPerUser || 0;
    if (userNumEntries >= maxNumEntriesPerUser) {
      setOpenEntryLimitReachedModal(true);
      return;
    }

    resetAllEntryStore();
    navigate(`/rounds/${roundId}/select-players`);
  };

  const entryButtonBackgroundColor = isEnterDisabled ? "bg-disabled" : "bg-gradient-primary";

  return (
    <div className="h-screen">
      <MatchName homeName={round.homeName} awayName={round.awayName} roundId={round.id} />
      <div
        className="flex flex-col items-center w-full h-auto bg-cover bg-top"
        style={{
          backgroundImage: "url('/assets/soccer-round-background.png')",
        }}
      >
        <RoundInfo
          entryCloseTime={round.entryCloseTime}
          title="キャプテン・副キャプテンを選ぶ"
          subTitle="最も活躍する選手をキャプテン、2番目に活躍する選手を副キャプテンにしよう！"
        />

        <div className="flex flex-col py-4 w-full">
          <div className="self-center w-full px-2 sm:px-10 md:px-14 lg:px-16 xl:px-20 2xl:px-24">
            <div className="bg-light-blue py-3 rounded-md">
              <div className="px-2 grid grid-cols-2">
                <div className="w-full flex flex-row gap-2">
                  <div className="place-content-center rounded-lg">
                    <div className="flex justify-center items-center w-[38px]">
                      {captain?.name ? (
                        <PlayerImage name={captain?.name || ""} url={captain?.imageUrl || ""} />
                      ) : (
                        <div className="w-[38px] h-[57px] bg-[#1F1C1C99] place-content-center rounded-lg">
                          <div className="flex justify-center items-center">
                            <ThumbaIcon color="darkGray" className="w-5" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <div className="flex w-[90px] justify-center px-2 py-1 text-white rounded-full bg-primary items-center">
                      <CaptainIcon className="w-[14px]" />
                      <span className="pl-1 text-[8px] font-extrabold content-center ">キャプテンシ</span>
                    </div>
                    <div className="flex">
                      <span className="pl-1 pt-1 text-primary text-[12px] font-bold whitespace-nowrap">
                        活躍ポイント2倍
                      </span>
                      <div className="content-end pl-1">
                        <UpGradientIcon />
                      </div>
                    </div>
                    <span
                      className={cn(
                        "pl-1 text-black font-bold text-[10px] text-[#2D2D2D]",
                        captain?.name ? "opacity-100" : "opacity-0",
                      )}
                    >
                      {captain?.name || ""}
                    </span>
                  </div>
                </div>
                {round.type === "Round0002" && (
                  <div className="w-full flex flex-row gap-2">
                    <div className="place-content-center rounded-lg">
                      <div className="flex justify-center items-center w-[38px]">
                        {viceCaptain?.name ? (
                          <PlayerImage name={viceCaptain?.name || ""} url={viceCaptain?.imageUrl || ""} />
                        ) : (
                          <div className="w-[38px] h-[57px] bg-[#1F1C1C99] place-content-center rounded-lg">
                            <div className="flex justify-center items-center">
                              <ThumbaIcon color="darkGray" className="w-5" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <div className="flex w-[90px] items-center justify-center px-2 py-1 text-white rounded-full bg-secondary">
                        <ViceCaptainIcon className="w-10" />
                        <span className="pl-1 text-[8px] whitespace-nowrap font-extrabold content-center">
                          副キャプテンシ
                        </span>
                      </div>
                      <div className="flex">
                        <span className="pl-1 pt-1 text-secondary text-xs font-bold">活躍ポイント1.5倍</span>
                        <div className="content-end pl-1">
                          <UpGradientIcon />
                        </div>
                      </div>
                      <span
                        className={cn(
                          "pl-1 text-black font-bold text-[10px] text-[#2D2D2D]",
                          viceCaptain?.name ? "opacity-100" : "opacity-0",
                        )}
                      >
                        {viceCaptain?.name || ""}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              {/* // Player cards */}
              <div className="mt-4">
                {entryDetails.playerIds.map((playerId, index) => {
                  const player = getPlayerById(playerId, [...round.players.homePlayers, ...round.players.awayPlayers]);
                  const isCaptain = captain?.id === playerId;
                  const isViceCaptain = viceCaptain?.id === playerId;
                  const captainSelectBackground = isCaptain ? "bg-primary" : "bg-disabled";
                  const viceCaptainSelectBackground = isViceCaptain ? "bg-secondary" : "bg-disabled";
                  if (!player) return null;

                  return (
                    <div className="flex flex-col divide-y" key={player.id}>
                      <div key={playerId} className="grid grid-cols-8 justify-center gap-3 py-2 items-center">
                        <div className="col-span-3 w-full flex flex-row gap-3">
                          <div className="max-w-14 place-content-center rounded-lg">
                            <PlayerImage
                              url={player.imageUrl}
                              name={player.name}
                              isCaptain={isCaptain}
                              isViceCaptain={isViceCaptain}
                              className="w-[50px]"
                            />
                          </div>
                          <div className="flex flex-col text-nowrap">
                            <div className="text-white">
                              <span className="font-bold text-sm">{player.name}</span>
                            </div>
                            <div className="flex text-[#91E9FF] ">
                              <span className="content-center text-[10px] font-bold">活躍ポイント :</span>
                              <span className="pl-[2px] text-[12px] font-extrabold">
                                {player.averageFantasyPoint.toFixed(2)} pts
                              </span>
                            </div>
                            <span className="text-[#91E9FF] text-[10px] font-normal">(直近8試合の平均)</span>

                            <PlayerDetailButton id={player.id} selectedPlayers={[]} roundId={roundId || ""} />
                          </div>
                        </div>
                        <div className="flex gap-2 col-span-5 justify-end self-end">
                          <div
                            className={cn(
                              "py-[12px] border border-white whitespace-nowrap text-white rounded-full cursor-pointer min-w-[86px] text-center font-bold text-xs",
                              captainSelectBackground,
                            )}
                            onClick={() => {
                              if (player.id === viceCaptain?.id) {
                                onSelectViceCaptain(null);
                              }

                              if (player.id !== captain?.id) {
                                onSelectCaptain(player);
                              }
                            }}
                          >
                            キャプテン
                          </div>

                          {round.type === "Round0002" && (
                            <div
                              className={cn(
                                "py-[12px] border border-white whitespace-nowrap text-white rounded-full cursor-pointer min-w-[86px] text-center font-bold text-xs",
                                viceCaptainSelectBackground,
                              )}
                              onClick={() => {
                                if (player.id === captain?.id) {
                                  onSelectCaptain(null);
                                }

                                if (player.id !== viceCaptain?.id) {
                                  onSelectViceCaptain(player);
                                }
                              }}
                            >
                              副キャプテン
                            </div>
                          )}
                        </div>
                      </div>
                      {index <= entryDetails.playerIds.length - 1 && (
                        <Separator orientation="horizontal" className="bg-white opacity-30 h-[0.5px]" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className={cn(
                "w-full text-base font-bold text-white rounded-lg text-center my-4 py-4 cursor-pointer",
                entryButtonBackgroundColor,
              )}
              onClick={handleOnClick}
            >
              エントリーを確定する
            </div>
          </div>
        </div>
      </div>

      {authState === "unauthenticated" && (
        <RequiredLoginModal open={openRequiredLoginModal} setOpen={setOpenRequiredLoginModal} />
      )}

      {authState === "authenticated" && (
        <>
          <ConfirmCreateEntryModal
            open={openConfirmCreateEntryModal}
            setOpen={setOpenConfirmCreateEntryModal}
            onConfirm={handleConfirmCreateEntry}
            roundEntryUnit={roundEntryUnit}
            userPoints={userPoints}
            userNumEntries={userNumEntries}
          />

          <CreateEntrySuccessfulModal
            open={openCreateEntrySuccessfulModal}
            setOpen={setOpenCreateEntrySuccessfulModal}
            onConfirm={handleConfirmCreateEntrySuccessful}
            resetAllEntryStore={resetAllEntryStore}
          />

          <InsufficientModal
            open={openInsufficientModal}
            setOpen={setOpenInsufficientModal}
            entryFee={Number.parseInt(roundEntryUnit.entryFee)}
            userPoints={userPoints}
          />

          <EntryLimitReachedModal
            open={openEntryLimitReachedModal}
            setOpen={setOpenEntryLimitReachedModal}
            userNumEntries={userNumEntries}
            maxNumEntriesPerUser={roundEntryUnit.maxNumEntriesPerUser || 0}
            resetAllEntryStore={resetAllEntryStore}
          />
        </>
      )}
    </div>
  );
};
